import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useConfigurationStore, useDateStore, useStateStore } from './store';
import { useParams } from "@cedarjs/router";
import { useAuth } from "src/auth";
import "src/fragments/review";

export const CONFIG_QUERY = gql`
  query ConfigQuery {
    tresholds {
      id
      unit
      extremeLow
      low
      target
      high
      extremeHigh

      minimum
      maximum
    }

    batchReportTargets {
      id
      name
      stadePL
      quantity
      doc
      docN
      docPG
      docG
      biomass
      avgWeight
      fcr
      growth
      survival
      yearlyProduction
      plsOnBiomass
      densitySurface
      sellableTargetProportion
      isDefault
    }
  }
`
export const SITE_QUERY = gql`
  query SiteInfosQuery($id: Int!) {
    site(id: $id) {
      id
      name
      address
    }
  }
`
export const TANK_QUERY = gql`
  query TankInfosQuery($id: Int!, $date: Date) {
    tank(id: $id, date: $date) {
      id
      name
      line
      tank_type {
        id
        name
        canHostBatch
      }
      width
      idWater
      waterHeight
      length
      lastReview {
        ...ReviewFragment
      }
      lastBatchTransfer {
        batch {
          id
          name
          quantity
          acclimatization {
            id
            createdAt
          }
          harvest {
            createdAt
          }
          report {
            createdAt
          }
          isStopped
          batchProductionForDate {
            biomass
            avgWeight
            daysOfCulture
            stadePL
            avgWeight_offsetModel
          }
          lastBatchProduction {
            date
            biomass
            avgWeight
            daysOfCulture
            stadePL
            avgWeight_offsetModel
          }
          batchInitial {
            id
            name
            
            createdAt
            quantity
            stadePL
            growModel {
              name
            }
            provider {
              name
            }
          }

          batchTransfers {
            id
            createdAt
            tankSrc {
              id
              name
              tank_type {
                name
              }
            }
            tankDest {
              id
              name
              tank_type {
                name
              }
            }
          }
        }
      }

      site {
        id
        name
      }
    }
  }
`
export const BATCH_QUERY = gql`
  query BatchInfosQuery($id: Int!, $date: Date) {
    batch(id: $id, date: $date) {
      id
      name
      line #Used in batchStop
      createdAt #Used in batchStop
      site {
        id
        name
        address
      }
      quantity
      idRootBatch
      acclimatization {
        id
        createdAt
      }
      rootBatch {
        batchInitial{
          provider {
            name
          }
        }
      }
      subBatches {
        id
        name
      }
      isHarvested
      isFrozen
      isStopped
      # productionsWithoutFeedings
      lastReview {
        ...ReviewFragment
      }
      totalDaysOfCulture
      totalDaysOfCultureBP
      aiRequestsCount
      harvest {
        id
        createdAt
      }
      report {
        id
        createdAt
        biomass
        fcr
        survival
        growth
        yearlyProduction
      }
      #Used in batchStop
      lastBatchTransfer {
        id
        createdAt
      }
      batchProductionForDate {
        biomass
        avgWeight
        daysOfCulture
        stadePL
        avgWeight_offsetModel
      }
      lastBatchProduction {
        biomass
        avgWeight
        daysOfCulture
        stadePL
        avgWeight_offsetModel
      }
      batchInitial {
        id
        name
        
        createdAt
        quantity
        stadePL
        growModel {
          name
        }
        provider {
          name
        }
      }
      currentTank {
        id
        name
        site {
          id
        }
      }

      batchTransfers {
        id
        createdAt
        tankSrc {
          id
          name
          idSite
          tank_type {
            name
          }
        }
        tankDest {
          id
          name
          idSite
          tank_type {
            name
          }
        }
      }
    }
  }
`

export const useLoadStateBasedOnRoute = ({ idSite = null, idTank = null, idBatch = null }: { idSite?: string, idTank?: string, idBatch?: string }) => {
  //Do not load anything until the auth is ready
  const {loading} = useAuth()
  const [setAllFromTank, setSite, resetAllFromTank, setAllFromBatch, batchReportTarget, setBatchReportTarget] = useStateStore(state => [state.setAllFromTank, state.setSite, state.resetAllFromTank, state.setAllFromBatch, state.batchReportTarget, state.setBatchReportTarget]);
  const [date, dateGQL] = useDateStore(state => [state.date, state.getDateForGQL()]);
  const [setTresholds, setBatchReportTargets] = useConfigurationStore(state => [state.setTresholds, state.setBatchReportTargets])

  const configQuery = useQuery(CONFIG_QUERY, {
    skip: loading
  });

  useEffect(() => {
    if (configQuery.data) {
      setTresholds(configQuery.data.tresholds);
      setBatchReportTargets(configQuery.data.batchReportTargets);
      //Set default batchReportTarget
      if (!batchReportTarget && configQuery.data.batchReportTargets?.length){
        const defaultTarget = configQuery.data.batchReportTargets.find(target => target.isDefault);
        setBatchReportTarget(defaultTarget);
      }
    }
  }, [configQuery.data]);

  const siteQuery = useQuery(SITE_QUERY, {
    variables: { id: parseInt(idSite || '0') },
    skip: loading || !idSite,
  });

  useEffect(() => {
    if (idSite && siteQuery.data && siteQuery.data.site) {
      setSite(siteQuery.data.site);
    }
    else if (!idSite) {
      setSite(null);
      resetAllFromTank();
    }
  }, [idSite, siteQuery.data, setSite]);

  const tankQuery = useQuery(TANK_QUERY, {
    variables: { id: parseInt(idTank || '0'), date: dateGQL },
    skip: loading || !idTank,
  });

  useEffect(() => {
    if (idTank && tankQuery.data && tankQuery.data.tank) {
      setAllFromTank(tankQuery.data.tank, date);
    }
    else if (!idTank) {
      resetAllFromTank();
    }
  }, [idTank, tankQuery.data, setAllFromTank, dateGQL]);

  const batchQuery = useQuery(BATCH_QUERY, {
    variables: { id: parseInt(idBatch || '0'), date: dateGQL },
    skip: loading || !idBatch,
  });

  useEffect(() => {
    if (idBatch && batchQuery.data && batchQuery.data.batch) {
      setAllFromBatch(batchQuery.data.batch, date);
    }
    else if (!idBatch) {
      resetAllFromTank();
    }
  }, [idBatch, batchQuery.data, setAllFromTank, dateGQL]);

  //Reset everything if there isn't any params
  useEffect(() => {
    if (!idSite && !idTank && !idBatch) {
      setSite(null);
      resetAllFromTank();
    }
  }, [idSite, idTank, idBatch]);


  return [siteQuery, tankQuery, batchQuery];
};